import { t } from "@/language/translation";
import {
  ChartInterval,
  type IChartCommonSeriesType,
  type IChartSeriesWithoutData,
  type Maybe,
} from "@/graphql";
import type { ChartEditType, ChartSeriesType } from "../chartTypes";

/**
 * the sorting(index setting) must be happen because of highcharts bug https://github.com/highcharts/highcharts/issues/15966
 * line series must be on top
 * @param index index of series
 * @param type type of series
 * @returns corrected index series
 */
export function correctSeriesIndex(
  type:
    | "area"
    | "arearange"
    | "areaspline"
    | "areasplinerange"
    | "column"
    | "columnrange"
    | "line"
    | "spline"
    | "pie",
  index: number,
) {
  // index + 100000 is for centerInCategory Bug and the other + 10000 at the end to prevent negative
  // indexes that forces highcharts to be not idempotent (sometimes the series is visible sometimes not)
  return (type === "line" ? index + 100000 : index) + 10000;
}

/**
 * gets the type of the series
 * @param series datapoint | target | calculation series
 * @returns string of series type
 */
export function typeofSeries(series: ChartSeriesType): "datapoint" | "target" | "calculation" {
  if ("dpConn" in series) {
    return "datapoint";
  }
  if ("datapointTypeId" in series) {
    return "target";
  }
  if ("calculationKeys" in series) {
    return "calculation";
  }
  throw Error("unknown series type");
}

/**
 * Type of the highcharts custom attribute on series points
 */
export type CustomSeriesPointType = {
  key: string;
  type: ReturnType<typeof typeofSeries>;
  yAxis: number;
  operator: string;
};

function getUnitFromInterval(interval?: ChartInterval) {
  switch (interval) {
    case ChartInterval.BUSINESS_YEAR:
    case ChartInterval.YEAR:
      return "y";
    case ChartInterval.MONTH:
      return "M";
    case ChartInterval.WEEK:
    case ChartInterval.SHIFT:
    case ChartInterval.DAY:
      return "d";
    case ChartInterval.HOUR:
      return "h";
    case ChartInterval.MINUTE:
      return "min";
    case ChartInterval.SECOND:
      return "s";
    default:
      return undefined;
  }
}

/**
 * get the unit of the series as formatted string
 * @param series the series to get the label for
 * @returns A string with a space and the unit of the series
 */
export function getSeriesUnitLabel(series: ChartSeriesType, previousData: boolean): string | null {
  let unit: Maybe<string> = "";
  if ("dpConn" in series) {
    unit = series.dpConn[0].datapointType?.unit;
    if ((unit == null || unit === "") && series.options.interpolateDatapoints) {
      unit = getUnitFromInterval(
        previousData
          ? (series.commonType.chartSeriesData.aggregationSecond?.interval ??
              series.commonType.chartSeriesData.aggregationSecond?.moving?.movingInterval)
          : (series.options.dataManipulationInterval ??
              series.commonType.chartSeriesData.aggregation?.interval ??
              series.commonType.chartSeriesData.aggregation?.moving?.movingInterval),
      );
    } else {
      unit = series.dpConn[0].datapointType?.unit;
    }
  } else if ("datapointTypeId" in series) {
    unit = series.datapointType?.unit;
  } else if ("calculationKeys" in series) {
    unit = series.unit;
  }
  if (unit == null || unit === "") {
    return null;
  }
  return ` ${unit}`;
}

/**
 * gets the display name of a series (it can change on shifts)
 * @param chartSettings settings of the complete chart
 * @param series the series to get name from
 * @param translate a function to translate strings
 * @returns the display name of the series
 */
export function getSeriesDisplayName(
  chartSettings: ChartEditType,
  series: ChartSeriesType,
): string {
  if ("scheduleRowType" in series && chartSettings.dataGranularity === ChartInterval.SHIFT) {
    const shiftRowName = t(`global.shift.${series.scheduleRowType}`).toString();
    return `${series.commonType.name} - ${shiftRowName}`;
  }
  return series.commonType.name;
}

export function getSeriesDisplayNameOnCalculation(
  chartSettings: ChartEditType,
  series: ChartSeriesType,
): string {
  if (series.commonType.name !== "") {
    return getSeriesDisplayName(chartSettings, series);
  }
  if (series.commonType.placeholder != null) {
    return t(series.commonType.placeholder).toString() ?? "";
  }
  return "";
}

/**
 * change a common prop in all series
 * @param allSeries all series that should be changed
 * @param prop the prop that will be changed
 * @param value the value for the props in each series
 */
export function changeChartSeriesDataProp<
  TObj extends IChartSeriesWithoutData,
  TProp extends keyof Omit<ChartSeriesType["commonType"]["chartSeriesData"], "__typename">,
>(allSeries: ChartSeriesType[], prop: TProp, value: TObj[TProp]) {
  allSeries.forEach((series) => {
    series.commonType.chartSeriesData[prop] = value;
  });
}

/**
 * change a common prop in all series
 * @param allSeries all series that should be changed
 * @param prop the prop that will be changed
 * @param value the value for the props in each series
 */
export function changeChartCommonTypeProp<
  TObj extends IChartCommonSeriesType,
  TProp extends keyof IChartCommonSeriesType,
>(allSeries: ChartSeriesType[], prop: TProp, value: TObj[TProp]) {
  allSeries.forEach((series) => {
    series.commonType[prop] = value;
  });
}

/**
 * removes shift from series (used for target and calculation)
 * @param commonType commonType of series
 */
export function removeShiftFromAggregation(commonType: IChartCommonSeriesType) {
  const agg = commonType.chartSeriesData.aggregation;

  if (agg?.moving?.movingInterval === ChartInterval.SHIFT) {
    agg.moving.movingInterval = ChartInterval.DAY;
  }
  if (agg?.interval === ChartInterval.SHIFT) {
    agg.interval = ChartInterval.DAY;
  }
}

export default {
  changeChartSeriesDataProp,
  changeChartCommonTypeProp,
  getSeriesDisplayName,
  getSeriesDisplayNameOnCalculation,
};
